/**
 * GitHubActionSetupWizard Component
 *
 * Setup wizard for adding LeftShield GitHub Action to workflows.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  makeStyles,
  tokens,
  Divider,
  MessageBar,
  MessageBarBody,
  Link,
  RadioGroup,
  Radio,
} from '@fluentui/react-components';
import {
  CopyRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minWidth: '650px',
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  codeBlock: {
    position: 'relative',
    fontFamily: 'monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
  },
  copyButton: {
    position: 'absolute',
    top: tokens.spacingVerticalXS,
    right: tokens.spacingHorizontalXS,
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

interface GitHubActionSetupWizardProps {
  open: boolean;
  onClose: () => void;
  apiKey?: string;
}

export const GitHubActionSetupWizard: React.FC<GitHubActionSetupWizardProps> = ({
  open,
  onClose,
  apiKey = 'YOUR_API_KEY',
}) => {
  const styles = useStyles();
  const [copiedStep, setCopiedStep] = useState<number | null>(null);
  const [failOn, setFailOn] = useState<'block' | 'warn'>('block');

  const copyToClipboard = (text: string, step: number) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(step);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const workflowYaml = `name: LeftShield Security Scan

on:
  pull_request:
    branches: [main, develop]
  push:
    branches: [main]

jobs:
  security-scan:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4

      - name: LeftShield Validation
        uses: leftshield/validate-code@v1
        with:
          api-key: \${{ secrets.LEFTSHIELD_API_KEY }}
          fail-on: ${failOn}
          scan-types: sast,secrets,sca
          annotations: true
          sarif-output: true

      - name: Upload SARIF
        if: always()
        uses: github/codeql-action/upload-sarif@v3
        with:
          sarif_file: leftshield-results.sarif`;

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Set Up GitHub Action</DialogTitle>
          <DialogContent className={styles.content}>
            <MessageBar intent="info">
              <MessageBarBody>
                The LeftShield GitHub Action validates code on pull requests and pushes, with support for SARIF reports and PR annotations.
              </MessageBarBody>
            </MessageBar>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>1</span>
                <Text weight="semibold">Add API Key to Repo Secrets</Text>
              </div>
              <Text size={200}>
                Go to your repository Settings → Secrets and variables → Actions → New repository secret
              </Text>
              <div className={styles.codeBlock}>
                <Text size={200}>
                  <strong>Name:</strong> LEFTSHIELD_API_KEY{'\n'}
                  <strong>Value:</strong> {apiKey}
                </Text>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 1 ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(apiKey, 1)}
                />
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>2</span>
                <Text weight="semibold">Configure Failure Behavior</Text>
              </div>
              <RadioGroup
                value={failOn}
                onChange={(_, data) => setFailOn(data.value as 'block' | 'warn')}
              >
                <div className={styles.option}>
                  <Radio value="block" label="Fail on blocking violations (recommended)" />
                  <Text size={200} style={{ marginLeft: '28px', color: tokens.colorNeutralForeground3 }}>
                    PRs will fail if critical/high severity issues are found
                  </Text>
                </div>
                <div className={styles.option}>
                  <Radio value="warn" label="Fail on any violations" />
                  <Text size={200} style={{ marginLeft: '28px', color: tokens.colorNeutralForeground3 }}>
                    PRs will fail for any enforcement decision
                  </Text>
                </div>
              </RadioGroup>
            </div>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>3</span>
                <Text weight="semibold">Create Workflow File</Text>
              </div>
              <Text size={200}>
                Create <code>.github/workflows/leftshield.yml</code> with the following content:
              </Text>
              <div className={styles.codeBlock}>
                <pre style={{ margin: 0 }}>{workflowYaml}</pre>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 3 ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(workflowYaml, 3)}
                />
              </div>
            </div>

            <Divider />

            <MessageBar intent="success">
              <MessageBarBody>
                Once configured, LeftShield will automatically scan code on every PR and push.{' '}
                <Link href="https://docs.leftshield.io/github-action" target="_blank">
                  View documentation
                </Link>
              </MessageBarBody>
            </MessageBar>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
